// src/pages/public/ProductDetailPage.jsx

import React, { useEffect, useState, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import api from "../../utils/axios";
import BottomNav from "../../components/layouts/BottomNav";
import { useCart } from "../../contexts/CartContext";
import { useToast } from "../../contexts/ToastContext";
import { formatPrice } from "../../utils/priceFormatter";
import { trackProductView } from "../../utils/analytics";
import { TransformWrapper, TransformComponent } from "react-zoom-pan-pinch";

import { API_BASE } from '../../utils/url';

const technicalFields = [
  { key: "product_id", label: "Group info/Product ID" },
  { key: "botanical_name", label: "Botanical Name" },
  { key: "origin", label: "Origin" },
  { key: "collection", label: "Collection" },
  { key: "grain", label: "Grain" },
  { key: "dimension", label: "Dimension" },
  { key: "veneer_thickness", label: "Veneer Thickness" },
  { key: "finish", label: "Finish" },
  { key: "substrate", label: "Substrate" },
  { key: "shade", label: "Shade" },
  { key: "color", label: "Color" },
];

const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, cartCount } = useCart();
  const { showSuccess, showError } = useToast();

  const [product, setProduct] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [userFreeze, setUserFreeze] = useState(null); // { total, latest, records }
  const [editFreeze, setEditFreeze] = useState({ isOpen: false, newQty: 1 });
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [sliderIndex, setSliderIndex] = useState(0);
  const sliderRef = useRef(null);
  const isAutoScrolling = useRef(false);


  // Parse images robustly from string/array/JSON
  const getImagesArray = (imgs) => {
    if (!imgs) return [];
    if (Array.isArray(imgs)) return imgs;
    if (typeof imgs === "string") {
      try {
        const parsed = JSON.parse(imgs);
        if (Array.isArray(parsed)) return parsed;
        return imgs.split(",").map((i) => i.trim()).filter(Boolean);
      } catch {
        return imgs.split(",").map((i) => i.trim()).filter(Boolean);
      }
    }
    return [];
  };

  const images = product ? getImagesArray(product.images) : [];

  // Auto Play Slider
  useEffect(() => {
    if (images.length <= 1 || isImageModalOpen) return; // Stop auto-play when modal is open
    const interval = setInterval(() => {
      setActiveImg(prev => (prev + 1) % images.length);
    }, 3000);
    return () => clearInterval(interval);
  }, [images.length, isImageModalOpen]);

  // Sync Slider Scroll
  useEffect(() => {
    if (sliderRef.current) {
      isAutoScrolling.current = true;
      const width = sliderRef.current.offsetWidth;
      sliderRef.current.scrollTo({ left: activeImg * width, behavior: 'smooth' });
      const timeout = setTimeout(() => {
        isAutoScrolling.current = false;
      }, 500);
      return () => clearTimeout(timeout);
    }
  }, [activeImg]);

  // Load product and related products
  useEffect(() => {
    setLoading(true);
    axios
      .get(`/products/${id}`)
      .then(async (res) => {
        setProduct(res.data);
        setActiveImg(0);

        // Track product view
        trackProductView(res.data.id, 'view');

        // Get category IDs from product
        const catIds = res.data.categories
          ? res.data.categories.map((c) => c.id)
          : [];
        // Get related products (excluding this one), first 8
        if (catIds.length) {
          const { data: allProducts } = await api.get("/products");
          const rel = allProducts.filter(
            (p) =>
              p.id !== res.data.id &&
              p.categories &&
              p.categories.some((cat) => catIds.includes(cat.id))
          );
          setRelated(rel.slice(0, 8));
        } else {
          setRelated([]);
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  // Load user's frozen record for this product
  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;
    api.get('/frozen-quantities/user').then(res => {
      const list = (res.data || []).filter(fq => String(fq?.product?.id) === String(id));
      if (list.length === 0) { setUserFreeze(null); return; }
      let latest = list[0];
      let total = 0;
      list.forEach(fq => {
        total += Number(fq.quantity || 0);
        if (new Date(fq.frozen_at) > new Date(latest.frozen_at)) latest = fq;
      });
      setUserFreeze({ total, latest, records: list });
    }).catch(() => { });
  }, [id]);

  if (loading) {
    return (
      <div style={{ minHeight: "100vh", background: "#fff" }}>
        <div style={{ textAlign: "center", padding: 44 }}>Loading...</div>
        <BottomNav />
      </div>
    );
  }

  if (!product) {
    return (
      <div style={{ minHeight: "100vh", background: "#fff" }}>
        <div style={{ textAlign: "center", padding: 44, color: "#888" }}>
          Product not found.
        </div>
        <BottomNav />
      </div>
    );
  }



  // Colors for "color" property (customize as needed)
  const colorSwatches = [
    "#F2D8B3", "#D2C0B5", "#85AAA7", "#888D95", "#25313D",
    "#EBA889", "#7B593E", "#BCA67A", "#25282E"
  ];

  return (
    <div style={{ background: "#fff", minHeight: "100vh", paddingBottom: 74 }}>
      {/* Header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        padding: "18px 18px 10px 18px",
        borderBottom: "1px solid #f3f3f3", background: "#fff", position: "sticky", top: 0, zIndex: 10
      }}>
        <div style={{ display: "flex", alignItems: "center" }}>
          <button
            onClick={() => navigate(-1)}
            style={{
              background: "none",
              border: "none",
              fontSize: 26,
              color: "#222",
              cursor: "pointer",
              lineHeight: 1
            }}
            aria-label="Back"
          >
            &larr;
          </button>
          <span style={{
            fontWeight: 700,
            fontSize: 22,
            marginLeft: 14
          }}>
            {product.name}
          </span>
        </div>

        {/* Cart Icon with Count */}
        <div
          onClick={() => navigate('/cart')}
          style={{
            position: "relative",
            cursor: "pointer"
          }}
        >
          <div style={{
            width: 40,
            height: 40,
            background: "#f5f5f5",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 18,
            color: "#333",
            fontWeight: "bold",
            border: "1px solid #e0e0e0"
          }}>
            🛒
          </div>
          {cartCount > 0 && (
            <div style={{
              position: "absolute",
              top: -5,
              right: -5,
              background: "#ef4444",
              color: "#fff",
              borderRadius: "50%",
              width: 20,
              height: 20,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 12,
              fontWeight: "bold"
            }}>
              {cartCount}
            </div>
          )}
        </div>
      </div>

      {/* Product Image Gallery */}
      <div style={{
        padding: "0",
        background: "#fff",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        marginBottom: 20
      }}>
        {/* Gallery Wrapper */}
        <div
          style={{
            width: "100%",
            aspectRatio: "1/1",
            borderRadius: "0 0 24px 24px",
            overflow: "hidden",
            background: "#f4f4f4",
            position: "relative",
          }}>

          {/* Scrollable Slider */}
          <div
            ref={sliderRef}
            onClick={() => setIsImageModalOpen(true)}
            onScroll={(e) => {
              if (!isAutoScrolling.current) {
                const width = e.target.offsetWidth;
                if (width > 0) {
                  const newIndex = Math.round(e.target.scrollLeft / width);
                  if (newIndex !== activeImg && newIndex >= 0 && newIndex < images.length) {
                    setActiveImg(newIndex);
                  }
                }
              }
            }}
            style={{
              width: "100%",
              height: "100%",
              display: "flex",
              overflowX: "scroll",
              overflowY: "hidden",
              scrollSnapType: "x mandatory",
              scrollbarWidth: "none",
              msOverflowStyle: "none",
              cursor: "pointer"
            }}
          >
            {images.length > 0 ? (
              images.map((img, idx) => (
                <div
                  key={idx}
                  style={{
                    minWidth: "100%",
                    height: "100%",
                    scrollSnapAlign: "center"
                  }}
                >
                  <img
                    src={
                      img.startsWith("http")
                        ? img
                        : API_BASE + img.replace(/^\/+/, "")
                    }
                    alt={`${product.name} - ${idx + 1}`}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block"
                    }}
                  />
                </div>
              ))
            ) : (
              <div style={{
                minWidth: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#aaa"
              }}>No image</div>
            )}
          </div>

          {/* Pagination Dots */}
          {images.length > 1 && (
            <div
              onClick={(e) => e.stopPropagation()}
              style={{
                position: "absolute",
                bottom: 12,
                left: "50%",
                transform: "translateX(-50%)",
                display: "flex",
                gap: 8,
                background: "rgba(0,0,0,0.2)",
                padding: "4px 8px",
                borderRadius: 12,
                backdropFilter: "blur(2px)",
                zIndex: 2
              }}
            >
              {images.map((_, idx) => (
                <div
                  key={idx}
                  onClick={() => setActiveImg(idx)}
                  style={{
                    width: 7,
                    height: 7,
                    borderRadius: "50%",
                    background: idx === activeImg ? "#fff" : "rgba(255,255,255,0.4)",
                    cursor: "pointer",
                    transition: "background 0.2s"
                  }}
                />
              ))}
            </div>
          )}

          {/* Expand Icon */}
          <div
            onClick={() => setIsImageModalOpen(true)}
            style={{
              position: "absolute",
              bottom: 12,
              right: 12,
              width: 32,
              height: 32,
              background: "rgba(0,0,0,0.3)",
              borderRadius: 6,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#fff",
              backdropFilter: "blur(2px)",
              cursor: "pointer",
              zIndex: 2
            }}
          >
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M15 3h6v6" />
              <path d="M9 21H3v-6" />
              <path d="M21 3l-7 7" />
              <path d="M3 21l7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Product description */}
      <div style={{
        padding: "0 14px 12px 14px", fontSize: 17, color: "#4a4a4a",
        fontWeight: 400, lineHeight: 1.55
      }}>
        {product.short_description && (
          <div style={{ marginBottom: 6 }}>{product.short_description}</div>
        )}

      </div>

      {/* Add to Cart Section */}
      <div style={{
        padding: "0 16px 20px 16px",
        borderBottom: "1px solid #f0f0f0",
        marginBottom: 20
      }}>
        {/* Price hidden as per user request */}

        {/* Stock Status */}
        <div style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: 16,
          padding: "8px 0"
        }}>
          <span style={{
            fontWeight: 600,
            color: "#333",
            fontSize: 16
          }}>Stock:</span>
          <span style={{
            fontWeight: 600,
            color: product.inventory > 5 ? "#22c55e" : product.inventory > 0 ? "#ef4444" : "#ef4444",
            fontSize: 16
          }}>
            {product.inventory > 0 ? `${product.inventory} in stock` : 'Out of stock'}
          </span>
        </div>

        {userFreeze && (
          <div style={{ display: 'flex', gap: 10, marginBottom: 16, alignItems: 'center' }}>
            <span
              onClick={() => setEditFreeze({ isOpen: true, newQty: userFreeze.total })}
              style={{ fontSize: 13, color: '#111', textDecoration: 'underline', cursor: 'pointer' }}
            >
              Edit freez
            </span>
            <div style={{ fontSize: 13, color: '#555' }}>
              Current: {userFreeze.total} • Expires: {new Date(userFreeze.latest.expires_at).toLocaleString('en-IN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
            </div>
          </div>
        )}

        {/* Quantity Selector */}
        <div style={{
          display: "flex",
          alignItems: "center",
          marginBottom: 16,
          gap: 12
        }}>
          <span style={{
            fontWeight: 500,
            color: "#757575",
            fontSize: 15
          }}>Quantity:</span>
          <div style={{
            display: "flex",
            alignItems: "center",
            border: "1px solid #ddd",
            borderRadius: 8,
            overflow: "hidden"
          }}>
            <button
              onClick={() => setQuantity(prev => Math.max(1, prev - 1))}
              style={{
                width: 40,
                height: 40,
                border: "none",
                background: "#f8f8f8",
                cursor: "pointer",
                fontSize: 18,
                fontWeight: "bold",
                color: "#333"
              }}
            >
              -
            </button>
            <span style={{
              width: 50,
              height: 40,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: "#fff",
              fontSize: 16,
              fontWeight: 500,
              borderLeft: "1px solid #ddd",
              borderRight: "1px solid #ddd"
            }}>
              {quantity}
            </span>
            <button
              onClick={() => setQuantity(prev => prev + 1)}
              style={{
                width: 40,
                height: 40,
                border: "none",
                background: "#f8f8f8",
                cursor: "pointer",
                fontSize: 18,
                fontWeight: "bold",
                color: "#333"
              }}
            >
              +
            </button>
          </div>
        </div>

        {/* Add to Cart Button */}
        <button
          onClick={() => {
            // Check if user is logged in
            // Login check removed to allow guest access


            // Check inventory
            if (product.inventory <= 0) {
              showError(`${product.name} is out of stock`);
              return;
            }

            if (product.inventory < quantity) {
              showError(`Only ${product.inventory} units available for ${product.name}`);
              return;
            }

            try {
              addToCart(product, quantity);
              showSuccess(`${product.name} added to cart!`);
              // Track add to cart interaction
              trackProductView(product.id, 'add_to_cart');
            } catch (error) {
              showSuccess('Failed to add item to cart');
            }
          }}
          disabled={product.inventory <= 0}
          style={{
            width: "100%",
            padding: "14px 20px",
            background: product.inventory <= 0 ? "#ccc" : "#333333",
            color: "#ffffff",
            border: "none",
            borderRadius: 8,
            fontSize: 16,
            fontWeight: 600,
            cursor: product.inventory <= 0 ? "not-allowed" : "pointer",
            transition: "background-color 0.2s ease",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.1)"
          }}
          onMouseEnter={(e) => {
            if (product.inventory > 0) {
              e.target.style.background = "#555555";
            }
          }}
          onMouseLeave={(e) => {
            if (product.inventory > 0) {
              e.target.style.background = "#333333";
            }
          }}
        >
          {product.inventory <= 0 ? 'Out of Stock' : 'Add to Cart'}
        </button>
      </div>


      {/* Color - Dynamic from Product Specifications */}
      {product.color && (
        <div style={{ padding: "0 16px", marginBottom: 18 }}>
          <div style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            padding: "8px 0"
          }}>
            <span style={{ fontWeight: 500, color: "#757575", fontSize: 15 }}>
              Color
            </span>
            <span style={{ fontSize: 16, color: "#333", fontWeight: 500 }}>
              {product.color}
            </span>
          </div>
        </div>
      )}

      {/* Technical Details Table */}
      <div style={{ padding: "0 16px 24px 16px" }}>
        <div style={{ fontWeight: 500, color: "#757575", fontSize: 15, marginBottom: 7 }}>
          Technical Details
        </div>
        <table style={{
          width: "100%",
          borderCollapse: "collapse",
          fontSize: 14,
          marginTop: 6,
          boxShadow: "0 1px 10px #0001",
          borderRadius: 10,
          overflow: "hidden"
        }}>
          <tbody>
            {technicalFields.map(field => (
              <tr key={field.key} style={{
                borderBottom: "1px solid #f0f0f0",
                background: "#fff"
              }}>
                <td style={{
                  padding: "10px 12px",
                  fontWeight: 500,
                  color: "#707070",
                  background: "#fcfcfc",
                  borderRadius: 0
                }}>{field.label}</td>
                <td style={{
                  padding: "10px 12px",
                  color: "#282828",
                  fontWeight: 400
                }}>
                  {product[field.key] ? product[field.key] : <span style={{ color: "#bbb" }}>—</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Other wood species (products from same categories) */}
      {related.length > 0 && (
        <div style={{ padding: "0 16px", marginBottom: 14 }}>
          <div style={{ fontWeight: 500, color: "#757575", fontSize: 15, marginBottom: 7 }}>
            Other Wood Species
          </div>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
            {related.slice(0, 9).map(prod => (
              <span
                key={prod.id}
                onClick={() => navigate(`/products/${prod.id}`)}
                style={{
                  fontSize: 14,
                  background: "#f6f6f7",
                  color: "#3f3f3f",
                  borderRadius: 14,
                  padding: "5px 13px",
                  marginBottom: 5,
                  border: "1.5px solid #eaeaea",
                  cursor: "pointer",
                  transition: "border 0.16s"
                }}
              >
                {prod.name}
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Related Products Slider */}
      {related.length > 0 && (
        <div style={{ padding: "0 12px 24px 12px", position: "relative" }}>
          <div style={{
            fontWeight: 500,
            color: "#757575",
            fontSize: 15,
            margin: "12px 0 12px 0"
          }}>Explore Similar Products</div>

          {/* Slider Container */}
          <div style={{ position: "relative" }}>
            {/* Left Arrow */}
            {related.length > 2 && (
              <button
                onClick={() => setSliderIndex(prev => prev === 0 ? Math.ceil(related.length / 2) - 1 : prev - 1)}
                style={{
                  position: "absolute",
                  left: "-8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 10,
                  background: "rgba(255,255,255,0.95)",
                  border: "1px solid #ddd",
                  borderRadius: "50%",
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  fontSize: "18px",
                  color: "#333",
                  padding: 0
                }}
              >
                ‹
              </button>
            )}

            {/* Products Grid */}
            <div
              className="related-products-grid-mobile"
              style={{
                display: "grid",
                gridTemplateColumns: "1fr 1fr",
                gap: 12,
                overflow: "hidden"
              }}
            >
              {related.slice(sliderIndex * 2, sliderIndex * 2 + 2).map(prod => {
                const relImgs = getImagesArray(prod.images);
                return (
                  <div
                    key={prod.id}
                    onClick={() => navigate(`/products/${prod.id}`)}
                    style={{
                      background: "#fcfcfd",
                      borderRadius: 10,
                      boxShadow: "0 1px 7px 0 rgba(40,44,63,0.04)",
                      padding: 8,
                      textAlign: "center",
                      cursor: "pointer"
                    }}>
                    <div style={{
                      width: "100%",
                      aspectRatio: "1/1",
                      borderRadius: 8,
                      overflow: "hidden",
                      background: "#eee",
                      marginBottom: 7
                    }}>
                      {relImgs[0] ? (
                        <img
                          src={
                            relImgs[0].startsWith("http")
                              ? relImgs[0]
                              : API_BASE + relImgs[0].replace(/^\/+/, "")
                          }
                          alt={prod.name}
                          style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        />
                      ) : (
                        <span style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          height: "100%",
                          color: "#bbb",
                          fontSize: 12
                        }}>No Image</span>
                      )}
                    </div>
                    <div style={{
                      fontWeight: 600,
                      fontSize: 14.5,
                      color: "#232323",
                      marginBottom: 2
                    }}>{prod.name}</div>
                  </div>
                );
              })}
            </div>

            {/* Right Arrow */}
            {related.length > 2 && (
              <button
                onClick={() => setSliderIndex(prev => (prev + 1) % Math.ceil(related.length / 2))}
                style={{
                  position: "absolute",
                  right: "-8px",
                  top: "50%",
                  transform: "translateY(-50%)",
                  zIndex: 10,
                  background: "rgba(255,255,255,0.95)",
                  border: "1px solid #ddd",
                  borderRadius: "50%",
                  width: "32px",
                  height: "32px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                  boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                  fontSize: "18px",
                  color: "#333",
                  padding: 0
                }}
              >
                ›
              </button>
            )}
          </div>
        </div>
      )}

      <BottomNav />

      {/* Edit Freeze Popup */}
      {editFreeze.isOpen && userFreeze && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }}>
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, width: '100%', maxWidth: 360 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 16 }}>Edit Freeze Quantity</div>
              <button onClick={() => setEditFreeze({ isOpen: false, newQty: 1 })} style={{ background: 'none', border: 'none', fontSize: 22, color: '#999', cursor: 'pointer' }}>×</button>
            </div>
            {userFreeze && (
              <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 8 }}>
                Previously: <strong>{userFreeze.total}</strong> unit{userFreeze.total > 1 ? 's' : ''} • Expires: {new Date(userFreeze.latest.expires_at).toLocaleString('en-IN', { year: 'numeric', month: '2-digit', day: '2-digit', hour: '2-digit', minute: '2-digit' })}
              </div>
            )}
            <div style={{ display: 'flex', gap: 8, alignItems: 'center', justifyContent: 'center', margin: '12px 0' }}>
              <button onClick={() => setEditFreeze(p => ({ ...p, newQty: Math.max(1, p.newQty - 1) }))} style={{ width: 40, height: 40, border: '2px solid #333', background: '#fff', borderRadius: '50%', fontSize: 20, fontWeight: 'bold', cursor: 'pointer' }}>−</button>
              <input
                type="number"
                min="1"
                value={editFreeze.newQty}
                onChange={(e) => {
                  const value = parseInt(e.target.value) || 1;
                  setEditFreeze(p => ({ ...p, newQty: Math.max(1, value) }));
                }}
                style={{
                  minWidth: 70,
                  height: 40,
                  textAlign: 'center',
                  fontSize: 18,
                  fontWeight: 600,
                  border: '2px solid #e9ecef',
                  borderRadius: 8,
                  outline: 'none',
                  padding: '0 8px'
                }}
                onFocus={(e) => e.target.select()}
              />
              <button onClick={() => setEditFreeze(p => ({ ...p, newQty: p.newQty + 1 }))} style={{ width: 40, height: 40, border: '2px solid #333', background: '#fff', borderRadius: '50%', fontSize: 20, fontWeight: 'bold', cursor: 'pointer' }}>+</button>
            </div>
            <div style={{ display: 'flex', gap: 10 }}>
              <button onClick={() => setEditFreeze({ isOpen: false, newQty: 1 })} style={{ flex: 1, padding: 10, background: '#f3f4f6', border: '1px solid #e5e7eb', borderRadius: 8, cursor: 'pointer' }}>Cancel</button>
              <button onClick={async () => {
                try {
                  // Re-fetch latest to avoid stale id
                  const latestRes = await api.get('/frozen-quantities/user');
                  const list2 = (latestRes.data || []).filter(fq => String(fq?.product?.id) === String(id));
                  let latest2 = userFreeze.latest; let total2 = userFreeze.total;
                  if (list2.length) {
                    latest2 = list2[0]; total2 = 0;
                    list2.forEach(fq => { total2 += Number(fq.quantity || 0); if (new Date(fq.frozen_at) > new Date(latest2.frozen_at)) latest2 = fq; });
                  }
                  const desired = editFreeze.newQty;
                  const delta = desired - total2;
                  const newLatestQty = Math.max(1, (latest2.quantity || 0) + delta);
                  await api.put(`/frozen-quantities/update-quantity/${latest2.id}`, { quantity: newLatestQty });
                  showSuccess('Freeze quantity updated');
                  setEditFreeze({ isOpen: false, newQty: 1 });
                  // refresh userFreeze
                  const res = await api.get('/frozen-quantities/user');
                  const list = (res.data || []).filter(fq => String(fq?.product?.id) === String(id));
                  if (list.length === 0) { setUserFreeze(null); return; }
                  let latest = list[0]; let total = 0;
                  list.forEach(fq => { total += Number(fq.quantity || 0); if (new Date(fq.frozen_at) > new Date(latest.frozen_at)) latest = fq; });
                  setUserFreeze({ total, latest, records: list });
                } catch (e) {
                  // Fallback: release all user freezes for this product and re-freeze desired total
                  try {
                    const latestRes2 = await api.get('/frozen-quantities/user');
                    const records = (latestRes2.data || []).filter(fq => String(fq?.product?.id) === String(id));
                    const desired = editFreeze.newQty;
                    for (const fq of records) {
                      try { await api.put(`/frozen-quantities/release/${fq.id}`); } catch { }
                    }
                    if (desired > 0) {
                      await api.post('/frozen-quantities/freeze', { product_id: Number(id), quantity: desired });
                    }
                    showSuccess('Freeze quantity updated');
                    setEditFreeze({ isOpen: false, newQty: 1 });
                    const res = await api.get('/frozen-quantities/user');
                    const list = (res.data || []).filter(fq => String(fq?.product?.id) === String(id));
                    if (list.length === 0) { setUserFreeze(null); return; }
                    let latest = list[0]; let total = 0;
                    list.forEach(fq => { total += Number(fq.quantity || 0); if (new Date(fq.frozen_at) > new Date(latest.frozen_at)) latest = fq; });
                    setUserFreeze({ total, latest, records: list });
                  } catch (fallbackErr) {
                    showError(fallbackErr.response?.data?.message || 'Failed to update');
                  }
                }
              }} style={{ flex: 1, padding: 10, background: '#000', color: '#fff', border: '1px solid #000', borderRadius: 8, cursor: 'pointer' }}>Save</button>
            </div>
          </div>
        </div>
      )}

      {/* Disclaimer Marquee */}
      <div className="marquee-wrapper">
        <div className="marquee-content">
          Disclaimer - Colour and quantity may vary &nbsp; • &nbsp; Disclaimer - Colour and quantity may vary &nbsp; • &nbsp; Disclaimer - Colour and quantity may vary &nbsp; • &nbsp; Disclaimer - Colour and quantity may vary &nbsp; • &nbsp; Disclaimer - Colour and quantity may vary
        </div>
      </div>

      {/* Full-Screen Image Preview Modal */}
      {isImageModalOpen && images[activeImg] && (
        <div
          onClick={() => setIsImageModalOpen(false)}
          style={{
            position: "fixed",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            background: "rgba(0, 0, 0, 0.95)",
            zIndex: 9999,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px"
          }}>
          {/* Close Button */}
          <button
            onClick={() => setIsImageModalOpen(false)}
            style={{
              position: "absolute",
              top: "20px",
              right: "20px",
              background: "rgba(255, 255, 255, 0.9)",
              border: "none",
              borderRadius: "50%",
              width: "40px",
              height: "40px",
              fontSize: "24px",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: "#000",
              fontWeight: "bold",
              zIndex: 10000,
              boxShadow: "0 2px 10px rgba(0,0,0,0.3)"
            }}
            aria-label="Close"
          >
            ×
          </button>

          {/* Left Arrow */}
          {images.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setActiveImg(prev => prev === 0 ? images.length - 1 : prev - 1);
              }}
              style={{
                position: "absolute",
                left: "20px",
                background: "rgba(255, 255, 255, 0.15)",
                border: "none",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                fontSize: "24px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                zIndex: 10000,
                transition: "background 0.2s"
              }}
              onMouseEnter={(e) => e.target.style.background = "rgba(255, 255, 255, 0.3)"}
              onMouseLeave={(e) => e.target.style.background = "rgba(255, 255, 255, 0.15)"}
            >
              &#8249;
            </button>
          )}

          {/* Full-Screen Image with Zoom */}
          <TransformWrapper
            initialScale={1}
            centerZoomedOut={true}
            wheel={{ step: 0.1 }}
            pinch={{ step: 5 }}
            doubleClick={{ step: 0.5 }}
          >
            <TransformComponent
              wrapperStyle={{ width: "100%", height: "100%" }}
              contentStyle={{ width: "100%", height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}
            >
              <img
                src={
                  images[activeImg].startsWith("http")
                    ? images[activeImg]
                    : API_BASE + images[activeImg].replace(/^\/+/, "")
                }
                alt={product?.name || "Product"}
                onClick={(e) => e.stopPropagation()}
                style={{
                  maxWidth: "100%",
                  maxHeight: "100%",
                  objectFit: "contain",
                  borderRadius: "8px",
                  margin: "0 50px" // Keep margin so arrows don't overlap image too much on small screens
                }}
              />
            </TransformComponent>
          </TransformWrapper>

          {/* Right Arrow */}
          {images.length > 1 && (
            <button
              onClick={(e) => {
                e.stopPropagation();
                setActiveImg(prev => (prev + 1) % images.length);
              }}
              style={{
                position: "absolute",
                right: "20px",
                background: "rgba(255, 255, 255, 0.15)",
                border: "none",
                borderRadius: "50%",
                width: "40px",
                height: "40px",
                fontSize: "24px",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                color: "#fff",
                zIndex: 10000,
                transition: "background 0.2s"
              }}
              onMouseEnter={(e) => e.target.style.background = "rgba(255, 255, 255, 0.3)"}
              onMouseLeave={(e) => e.target.style.background = "rgba(255, 255, 255, 0.15)"}
            >
              &#8250;
            </button>
          )}
        </div>
      )}
    </div>
  );
};

export default ProductDetailPage;
